import { CHAIN_CONFIGS } from '@/constants/chains'
import { TTradeSetting } from '@/data/settings'
import i18n from '@/i18n'
import {
  IEvent,
  IToken,
  ITrade,
  ITradeRes,
  IVaultDeposit,
  PENDING_ACTIONS,
  ProcessedTrade,
  REFUND_ACTIONS,
  TIMEOUT_ACTIONS,
  TradeAction,
  TradeStatus,
} from '@/types'
import { isBtcChain, isEvmChain } from '@/utils/chain'
import { SUPPORTED_NETWORK } from '@/utils/constants'
import { formatTokenAmount, parseAmount, truncateDecimals } from '@/utils/number'
import { findTokenByAddressAndChainId } from '@/utils/token'
import { truncateAddress } from '@/utils/truncate'

import { isEmpty } from 'lodash-es'

const IS_ACROSS_EVM_REFUND = !!new URLSearchParams(window.location.search).get('acrossEvmRefund')
const IS_TRADE_TIMEOUT = !!new URLSearchParams(window.location.search).get('timeout')

export const ensureHexPrefix = (value: string = '') => {
  return value.startsWith('0x') ? value : `0x${value}`
}

export const removeHexPrefix = (value: string) => {
  return value.startsWith('0x') ? value.slice(2) : value
}

export const formatRateString = (
  fromToken: IToken | undefined | null,
  toToken: IToken | undefined | null,
  exchangeRate: number | string | null
): string => {
  if (exchangeRate === null || !fromToken || !toToken) {
    return '-'
  }
  return `1 ${fromToken.tokenSymbol} ≈ ${formatTokenAmount(exchangeRate, toToken.tokenSymbol)} ${toToken.tokenSymbol}`
}

export const getTradeProgress = (state: TradeAction, events: IEvent[] = []): number => {
  if (REFUND_ACTIONS.includes(state)) {
    return -2
  }

  const hasEvent = (action: TradeAction) => events?.find((event) => event.action === action)
  if (hasEvent(TradeAction.TIMEOUT)) {
    return -1
  }
  if (
    hasEvent(TradeAction.MAKE_PAYMENT) ||
    hasEvent(TradeAction.CONFIRM_PAYMENT) ||
    hasEvent(TradeAction.CONFIRM_SETTLEMENT)
  ) {
    return 3
  }
  if (hasEvent(TradeAction.CONFIRM_DEPOSIT)) {
    return 2
  }

  switch (state) {
    case TradeAction.INIT:
    case TradeAction.SUBMIT_TRADE_INFO:
    case TradeAction.PRE_SUBMIT:
    case TradeAction.DEPOSITED:
    case TradeAction.SUBMITTED_TO_SOLVER:
    case TradeAction.CONFIRM_DEPOSIT:
      return 1
    case TradeAction.SELECT_PMM:
      return 2
    default:
      return 0
  }
}

export const formatTxHash = (txHash: string, network: string) => {
  if (!txHash) return ''
  if (isBtcChain(network)) {
    return removeHexPrefix(txHash)
  }
  if (isEvmChain(network)) {
    return txHash.startsWith('0x') ? txHash : '0x' + txHash
  }
  return txHash
}

export const toTxBlockExplorer = (network: string, txHash: string, path: 'tx' | 'address' = 'tx'): string => {
  const wrappedTxHash = formatTxHash(txHash, network)

  const customPaths: Record<string, Record<string, string>> = {
    [SUPPORTED_NETWORK.SOLANA]: { address: 'account' },
    [SUPPORTED_NETWORK.SOLANA_DEVNET]: { address: 'account' },
  }
  const type = customPaths?.[network]?.[path] || path

  return CHAIN_CONFIGS[network]?.getExplorerUrl(type, wrappedTxHash) ?? ''
}

// Format long IDs to show first 4 and last 4 characters
const formatId = (id: string): string => {
  if (id.length <= 8) return id
  return `0x${id.slice(0, 4)}...${id.slice(-4)}`
}

// Generate the message for trade signature
export const getMessageToTrade = ({
  tradeId,
  fromAddress,
  toUserAddress,
}: {
  tradeId: string
  fromAddress: string
  toUserAddress: string
}) => {
  // Remove any existing '0x' prefix before formatting
  const cleanId = tradeId.replace(/^0x/, '')
  const formattedId = formatId(cleanId)
  return `Sign to authenticate swap request for SwapID ${formattedId}. No fund is moved. \n\nI confirm that I, or the entity I represent, control and have full ownership over Sending Address ${truncateAddress(fromAddress)}${toUserAddress && ` and the Receiving address ${truncateAddress(toUserAddress)}`}.`
}

function getTradeStatusInfoData(step: { title: string; subtitle: string }, loadingProgress: number) {
  return {
    ...step,
    loadingProgress,
  }
}

const progressMap: Partial<Record<TradeAction, number>> = {
  [TradeAction.INIT]: 10,
  [TradeAction.PRE_SUBMIT]: 20,
  [TradeAction.DEPOSITED]: 30,
  [TradeAction.SUBMITTED_TO_SOLVER]: 40,
  [TradeAction.SUBMIT_TRADE_INFO]: 50,
  [TradeAction.CONFIRM_DEPOSIT]: 60,
  [TradeAction.SELECT_PMM]: 80,
  [TradeAction.MAKE_PAYMENT]: 90,
  [TradeAction.CONFIRM_PAYMENT]: 100,
}

export const getTitleByType = ({
  state,
  isDetail,
  step,
}: {
  state: TradeAction
  isDetail?: boolean
  step?: number
}) => {
  const textDetail = isDetail
    ? i18n.t('tradeStatus.REQUESTING_QUOTES_FROM_MM')
    : i18n.t('tradeStatus.REQUESTING_QUOTES')

  const map: Record<string, string> = {
    [TradeAction.CONFIRM_DEPOSIT]: textDetail,
    [TradeAction.RENEW_QUOTE]: step === 2 ? textDetail : i18n.t('tradeStatus.RETRY'),
    [TradeAction.TIMEOUT]: step === 2 ? textDetail : i18n.t('tradeStatus.TIMEOUT'),

    [TradeAction.INIT]: i18n.t('tradeStatus.DEPOSIT'),
    [TradeAction.PRE_SUBMIT]: i18n.t('tradeStatus.DEPOSIT'),
    [TradeAction.SUBMIT_TRADE_INFO]: i18n.t('tradeStatus.DEPOSIT'),
    [TradeAction.DEPOSITED]: i18n.t('tradeStatus.DEPOSIT'),
    [TradeAction.SUBMITTED_TO_SOLVER]: i18n.t('tradeStatus.DEPOSIT'),

    [TradeAction.SELECT_PMM]: step === 2 ? textDetail : i18n.t('tradeStatus.SETTLE'),
    [TradeAction.MAKE_PAYMENT]: step === 2 ? textDetail : i18n.t('tradeStatus.SETTLE'),

    [TradeAction.CONFIRM_PAYMENT]: step === 2 ? textDetail : i18n.t('tradeStatus.COMPLETED'),
    [TradeAction.CONFIRM_SETTLEMENT]: step === 2 ? textDetail : i18n.t('tradeStatus.COMPLETED'),

    [TradeAction.PRE_REFUND]: step === 2 ? textDetail : i18n.t('tradeStatus.UNLOCKING_VAULT'),
    [TradeAction.REFUNDED]: step === 2 ? textDetail : i18n.t('tradeStatus.UNLOCKED_VAULT'),
    [TradeAction.REFUND]: step === 2 ? textDetail : i18n.t('tradeStatus.UNLOCKED_VAULT'),
    [TradeAction.CONFIRM_REFUND]: step === 2 ? textDetail : i18n.t('tradeStatus.UNLOCKED_VAULT'),
  }
  return map[state] || state
}

export const getTradeTextColorClass = ({ state, step }: { state: TradeAction; step: number }): string => {
  if ([...TIMEOUT_ACTIONS, TradeAction.UNKNOWN, TradeAction.REPORT_FAILURE].includes(state)) return 'text-red-100'
  if (step >= 4 || (REFUND_ACTIONS.includes(state) && state !== TradeAction.PRE_REFUND)) return 'text-green-100'
  return 'text-main-100'
}
export const getTradeStatus = ({ status }: { status: TradeStatus }) => {
  const statusMap: Record<string, string> = {
    [TradeStatus.TIMEOUT]: i18n.t('tradeStatus.TIMEOUT'),
  }
  return statusMap[status]
}
export const getTradeStatusTextColorClass = ({ status }: { status: TradeStatus }): string | undefined => {
  if ([TradeStatus.TIMEOUT].includes(status)) return 'text-red-100'
  return undefined
}
export const getTradeStatusInfo = ({
  state,
  step,
  isDetail,
  status,
}: {
  state: TradeAction
  step: number
  isDetail: boolean
  status: string
}) => {
  const loadingProgress = progressMap[state] ?? 0

  const STATUSES = {
    CONFIRMED: i18n.t('tradeStatus.CONFIRMED'),
    PENDING: i18n.t('tradeStatus.PENDING'),
    IN_TRANSIT: i18n.t('tradeStatus.IN_TRANSIT'),
    PREPARING: i18n.t('tradeStatus.PREPARING'),
  }

  const ACTION_GROUPS = {
    IN_TRANSIT: [TradeAction.INIT, TradeAction.PRE_SUBMIT, TradeAction.DEPOSITED, TradeAction.SUBMITTED_TO_SOLVER],
    PENDING: [TradeAction.SUBMIT_TRADE_INFO, ...PENDING_ACTIONS],
    TIMEOUT_ACTIONS: [TradeAction.TIMEOUT, TradeAction.TIMEOUT_NO_DEPOSIT, ...REFUND_ACTIONS],
  }

  let titleStep2 = isDetail ? i18n.t('tradeStatus.REQUEST_QUOTES_FROM_MM') : i18n.t('tradeStatus.REQUEST_QUOTES')
  if (state === TradeAction.CONFIRM_DEPOSIT) {
    titleStep2 = isDetail ? i18n.t('tradeStatus.REQUESTING_QUOTES_FROM_MM') : i18n.t('tradeStatus.REQUESTING_QUOTES')
  }

  const TITLES = {
    STEP_0_1: i18n.t('tradeStatus.DEPOSIT'),
    STEP_2: titleStep2,
    STEP_3_PLUS: i18n.t('tradeStatus.SETTLE'),
  }

  if (ACTION_GROUPS.TIMEOUT_ACTIONS.includes(state) && step < 1) {
    return getTradeStatusInfoData({ title: '', subtitle: '' }, loadingProgress)
  }

  let title = ''
  if (step >= 0 && step <= 1) {
    title = TITLES.STEP_0_1
  } else if (step === 2) {
    title = TITLES.STEP_2
  } else if (step >= 3) {
    title = TITLES.STEP_3_PLUS
  }

  let subtitle = ''
  if (step >= 0 && step <= 1) {
    if (status === 'success') {
      subtitle = STATUSES.CONFIRMED
    } else if (ACTION_GROUPS.IN_TRANSIT.includes(state)) {
      subtitle = STATUSES.IN_TRANSIT
    } else if (ACTION_GROUPS.PENDING.includes(state)) {
      subtitle = STATUSES.PENDING
    } else {
      subtitle = STATUSES.CONFIRMED
    }
  } else if (step === 2) {
    subtitle = ''
  } else if (step >= 3) {
    if (state === TradeAction.SELECT_PMM) {
      subtitle = STATUSES.PREPARING
    } else if ([TradeAction.MAKE_PAYMENT, ...PENDING_ACTIONS].includes(state)) {
      subtitle = STATUSES.PENDING
    } else {
      subtitle = STATUSES.CONFIRMED
    }
  }

  return getTradeStatusInfoData({ title, subtitle }, loadingProgress)
}

export const tokenNeedApprove = (token: IToken | undefined) => {
  return token?.tokenAddress !== 'native' && isEvmChain(token?.networkId)
}

export const calculateMinAmountOut = (slippageValue: number, amountOut: string, toToken: IToken) => {
  if (!amountOut) return '0'
  if (IS_ACROSS_EVM_REFUND || IS_TRADE_TIMEOUT) {
    return truncateDecimals(parseAmount(amountOut) * (10 + slippageValue / 100), toToken?.tokenDecimals)
  }
  const result = truncateDecimals(parseAmount(amountOut) * (1 - slippageValue / 100), toToken?.tokenDecimals)
  return result
}

export const formatPercentage = (value: number): number => {
  if (value === 0) return 0

  const percentage = value * 100
  const absPercentage = Math.abs(percentage)

  if (absPercentage >= 0.01) {
    return parseAmount(percentage.toFixed(2))
  } else if (absPercentage < 0.001) {
    const rounded = parseAmount(percentage.toPrecision(4))
    return Math.abs(rounded) < 0.0001 ? 0 : rounded
  } else {
    return parseAmount(percentage.toFixed(4))
  }
}

export const isShowRetryButton = (tradeDetail: ITrade | undefined): boolean => {
  const now = Date.now() / 1000

  if (!tradeDetail?.events?.length) return false

  const { scriptTimeout, events, timestamp = 0, tradeTimeout } = tradeDetail
  const isWithinTimeout = now < scriptTimeout

  if (!isWithinTimeout || events.find((event) => event.action === TradeAction.SELECT_PMM)) return false

  const latestEvent = events.sort((a, b) => b.timestamp - a.timestamp)[0]
  const latestAction = latestEvent?.action
  const latestTxId = latestEvent?.txId
  if (!latestTxId) return false

  const renewQuoteEvents = events.filter((event) => event.action === TradeAction.RENEW_QUOTE)

  if (latestAction === TradeAction.RENEW_QUOTE) {
    const lastRenewTime = latestEvent.timestamp
    const canRenewAgain = now > lastRenewTime + 7200 // now > lastRenewTime + 2h
    if (!canRenewAgain) return false
  }

  let referenceTimestamp = timestamp
  if (renewQuoteEvents.length > 0) {
    const latestRenewQuote = renewQuoteEvents.sort((a, b) => b.timestamp - a.timestamp)[0]
    referenceTimestamp = latestRenewQuote.timestamp
  }

  const isOneHourPassed = now > referenceTimestamp + 3600 // now > lasted renew quote + 1h

  const isEligibleAction = [
    // TradeAction.DEPOSITED,
    // TradeAction.SUBMITTED_TO_SOLVER,
    TradeAction.SUBMIT_TRADE_INFO,
    TradeAction.CONFIRM_DEPOSIT,
  ].includes(latestAction)

  if (TradeAction.CONFIRM_DEPOSIT === latestAction && latestEvent.timestamp + 600 <= now) {
    return true
  }

  const maxAllowedTradeTimeout = scriptTimeout - 28800 //8h

  const newTradeTimeout = tradeTimeout + 7200
  const isValidTradeTimeout = newTradeTimeout <= maxAllowedTradeTimeout

  return isEligibleAction && isOneHourPassed && isValidTradeTimeout
}

export const canShowFastTrade = ({ tradeDetail }: { tradeDetail: ITrade }) => {
  const now = Date.now() / 1000
  const initTime = tradeDetail?.events?.find((e) => e.action === TradeAction.INIT)?.timestamp ?? 0

  // solana need a few second to have `presign` from api, so we need to wait a few seconds
  const mins = 1
  const threshold = (mins + 5) * 60 // actual waits at least `mins` minutes because backend auto -5 mins for INIT timestamp

  const missingPresign = !tradeDetail?.events?.find((e) => e.action === TradeAction.DEPOSITED)?.inputData?.presigns
    ?.length

  return (
    tradeDetail?.scriptTimeout > now &&
    tradeDetail?.status === TradeStatus.DEPOSITED &&
    missingPresign &&
    initTime + threshold <= now
  )
}

export const canShowFastTradeDeposit = ({ vaultDetail }: { vaultDetail: IVaultDeposit | undefined }) => {
  const now = Date.now() / 1000

  const depositedEvents = vaultDetail?.events?.find((e) => e.action === TradeAction.DEPOSITED)

  return (vaultDetail?.scriptTimeout ?? 0) > now && !vaultDetail?.depositTx && depositedEvents
}

export function getListTradeItemsData(listTrade: ITradeRes['data'] = [], listToken: IToken[]) {
  if (isEmpty(listTrade)) return []

  const listItems = listTrade?.map((trade: ITrade): ProcessedTrade => {
    let lastEvent: IEvent | null = null
    let fromToken: IToken | null = null
    let toToken: IToken | null = null
    let minAmountOut = '0'
    let amountOut = '0'
    let amountIn = '0'
    let tradeTimeout = trade.tradeTimeout

    if (trade.events && trade.events.length > 0) {
      const preSubmitEvent = trade.events.find((event) =>
        [TradeAction.PRE_SUBMIT, TradeAction.INIT].includes(event.action)
      )
      if (preSubmitEvent) {
        amountIn = preSubmitEvent.inputData?.amountIn || '0'
        minAmountOut = preSubmitEvent.inputData?.minAmountOut || '0'
        fromToken = preSubmitEvent.inputData?.fromToken as IToken
        toToken = preSubmitEvent.inputData?.toToken as IToken
      }

      if (!fromToken || !toToken) {
        const submitTradeInfoEvent = trade.events.find((event) => event.action === TradeAction.SUBMIT_TRADE_INFO)
        if (submitTradeInfoEvent) {
          if (submitTradeInfoEvent.inputData?.data?.tradeInfo.fromChain) {
            fromToken = findTokenByAddressAndChainId(
              submitTradeInfoEvent.inputData?.data?.tradeInfo.fromChain[2] || '',
              submitTradeInfoEvent.inputData?.data?.tradeInfo.fromChain[1] || '',
              listToken
            )
          }

          toToken = findTokenByAddressAndChainId(
            submitTradeInfoEvent.inputData?.data?.tradeInfo.toChain[2] || '',
            submitTradeInfoEvent.inputData?.data?.tradeInfo.toChain[1] || '',
            listToken
          )
        }
      }

      lastEvent = trade.events[trade.events.length - 1]
      if (!fromToken || !toToken) {
        const tradeInfo = lastEvent.inputData?.data?.tradeInfo

        if (tradeInfo) {
          const fromNetwork = tradeInfo.fromChain ? tradeInfo.fromChain[1] : SUPPORTED_NETWORK.BTC_TESTNET
          const fromTokenAddress = tradeInfo.fromChain ? tradeInfo.fromChain[2] : 'native'
          const toNetwork = tradeInfo.toChain[1]
          const toTokenAddress = tradeInfo.toChain[2]

          fromToken = findTokenByAddressAndChainId(fromTokenAddress, fromNetwork, listToken)
          toToken = findTokenByAddressAndChainId(toTokenAddress, toNetwork, listToken)
        }
      }

      const selectedPMMEvent = trade.events.find((event: IEvent) => event.action === TradeAction.SELECT_PMM)
      if (selectedPMMEvent?.inputData?.info) {
        const { pmmInfo, rfqInfo } = selectedPMMEvent.inputData.info
        if (!amountIn) {
          minAmountOut = rfqInfo.minAmountOut
        }
        amountOut = pmmInfo.amountOut
        if (rfqInfo.tradeTimeout) {
          tradeTimeout = Number(rfqInfo.tradeTimeout)
        }
      }
    }

    return { ...trade, lastEvent, fromToken, toToken, minAmountOut, amountOut, amountIn, tradeTimeout }
  })
  return listItems
}

export const getTransactionSlipPage = (minAmountOut: number, amountOut: number): number => {
  try {
    if (!minAmountOut || !amountOut) return 0
    const slippage = (1 - minAmountOut / amountOut) * 100
    return parseAmount(Math.max(0, slippage).toFixed(1))
  } catch {
    return 0
  }
}

export function getTradeSize({ tradeSetting, amountInUsd }: { tradeSetting: TTradeSetting; amountInUsd: number }) {
  const isBigTrade = !!tradeSetting && amountInUsd > tradeSetting.softCap && amountInUsd < tradeSetting.hardCap
  return {
    isBigTrade,
  }
}
export const isEthereumNetwork = (networkId: string): boolean => {
  return [SUPPORTED_NETWORK.ETHEREUM, SUPPORTED_NETWORK.ETHEREUM_SEPOLIA].includes(networkId as SUPPORTED_NETWORK)
}
